<template>
  <el-card shadow="never" v-loading="loading">
    <div slot="header" class="property-header">
      <span>Template Variables</span>
      <div>
        <el-button size="small" @click="loadProperties">
          Refresh
        </el-button>
        <el-button size="small" type="primary" @click="openCreateDialog">
          New Variable
        </el-button>
      </div>
    </div>

    <el-table :data="properties" stripe empty-text="No properties found">
      <el-table-column prop="label" label="Label" min-width="180" />
      <el-table-column prop="key" label="Key" min-width="180" />
      <el-table-column prop="type" label="Type" width="120" />
      <el-table-column prop="format" label="Format" width="120" />
      <el-table-column prop="sourceModel" label="Source" width="140" />
      <el-table-column prop="sourcePath" label="Source Path" min-width="180" />
      <el-table-column prop="fallbackValue" label="Fallback" min-width="180" />
      <el-table-column label="Status" width="110">
        <template slot-scope="{ row }">
          <el-tag size="small" :type="row.status === 'active' ? 'success' : 'info'">
            {{ row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="120">
        <template slot-scope="{ row }">
          <el-button size="mini" @click="openEditDialog(row)">
            Edit
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="60%"
      destroy-on-close
    >
      <el-form label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Label">
              <el-input v-model="form.label" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Key">
              <el-input v-model="form.key" placeholder="customer_name" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="Type">
              <el-select v-model="form.type" style="width: 100%;" @change="handleTypeChange">
                <el-option label="String" value="string" />
                <el-option label="Number" value="number" />
                <el-option label="Boolean" value="boolean" />
                <el-option label="Date" value="date" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Format">
              <el-select v-model="form.format" style="width: 100%;">
                <el-option label="None" value="none" />
                <el-option label="Currency" value="currency" />
                <el-option label="Date" value="date" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Fallback Value">
              <el-input v-model="form.fallbackValue" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Status">
              <el-select v-model="form.status" style="width: 100%;">
                <el-option label="Active" value="active" />
                <el-option label="Archived" value="archived" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="Source">
              <el-select v-model="form.sourceModel" style="width: 100%;">
                <el-option label="Manual" value="manual" />
                <el-option label="Customer" value="customer" />
                <el-option label="Facility" value="facility" />
                <el-option label="Account" value="account" />
                <el-option label="Invoice" value="invoice" />
                <el-option label="Transaction" value="transaction" />
                <el-option label="Lease" value="lease" />
                <el-option label="Unit" value="unit" />
                <el-option label="Payment" value="payment" />
                <el-option label="Refund" value="refund" />
                <el-option label="Transfer" value="transfer" />
                <el-option label="Move-out" value="moveout" />
                <el-option label="Event" value="event" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item label="Source Path">
              <el-input
                v-model="form.sourcePath"
                placeholder="primaryContact.name"
                :disabled="form.sourceModel === 'manual'"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Description">
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </el-form-item>

        <div class="property-actions">
          <el-button @click="dialogVisible = false">
            Cancel
          </el-button>
          <el-button type="primary" @click="saveProperty" :loading="saving">
            Save
          </el-button>
        </div>
      </el-form>
    </el-dialog>
  </el-card>
</template>

<script>
import {
  createEditorEmailTemplateProperty,
  listEditorEmailTemplateProperties,
  updateEditorEmailTemplateProperty,
} from '../utils/commonApi';

function getPropertyFallbackValue(property) {
  if (property.fallbackValue !== undefined) {
    return property.fallbackValue;
  }

  if (property.defaultValue !== undefined) {
    return property.defaultValue;
  }

  return property.sampleValue;
}

function getPropertyFormat(property, type) {
  if (['none', 'currency', 'date'].includes(property.format)) {
    return property.format;
  }

  if (type === 'date') {
    return 'date';
  }

  return 'none';
}

function normalizeProperty(property) {
  const { _id: objectId } = property;
  const fallbackValue = getPropertyFallbackValue(property);
  const type = ['string', 'number', 'boolean', 'date'].includes(property.type)
    ? property.type
    : 'string';
  const format = getPropertyFormat(property, type);
  return {
    ...property,
    id: property.id || objectId || '',
    type,
    format,
    fallbackValue: fallbackValue === undefined || fallbackValue === null ? '' : fallbackValue,
    sourceModel: property.sourceModel || 'manual',
    sourcePath: property.sourcePath || '',
  };
}

function createEmptyProperty() {
  return {
    label: '',
    key: '',
    type: 'string',
    format: 'none',
    description: '',
    fallbackValue: '',
    sourceModel: 'manual',
    sourcePath: '',
    status: 'active',
  };
}

export default {
  name: 'EmailTemplatePropertyManager',
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      isEditing: false,
      properties: [],
      form: createEmptyProperty(),
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditing ? 'Edit Template Variable' : 'Create Template Variable';
    },
  },
  async mounted() {
    await this.loadProperties();
  },
  methods: {
    async loadProperties() {
      try {
        this.loading = true;
        const properties = await listEditorEmailTemplateProperties(this.$keycloak.token, {
          includeArchived: true,
        });
        this.properties = properties.map(normalizeProperty);
      } catch (err) {
        this.$message.error({
          title: 'Unable to load properties',
          message: err.message,
        });
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.isEditing = false;
      this.form = createEmptyProperty();
      this.dialogVisible = true;
    },
    openEditDialog(property) {
      this.isEditing = true;
      this.form = {
        ...createEmptyProperty(),
        ...JSON.parse(JSON.stringify(normalizeProperty(property))),
      };
      this.dialogVisible = true;
    },
    handleTypeChange(type) {
      if (type === 'date' && (!this.form.format || this.form.format === 'none')) {
        this.form.format = 'date';
      }
    },
    async saveProperty() {
      if (!this.form.label) {
        this.$message.warning({
          title: 'Missing fields',
          message: 'Variable label is required.',
        });
        return;
      }

      if (this.form.sourceModel !== 'manual' && !this.form.sourcePath) {
        this.$message.warning({
          title: 'Missing fields',
          message: 'Source path is required when source is not manual.',
        });
        return;
      }

      try {
        this.saving = true;
        const payload = {
          label: this.form.label,
          key: this.form.key,
          type: this.form.type,
          format: this.form.format || 'none',
          description: this.form.description,
          fallbackValue: this.form.fallbackValue,
          sourceModel: this.form.sourceModel,
          sourcePath: this.form.sourceModel === 'manual' ? '' : this.form.sourcePath,
          status: this.form.status,
        };
        if (this.isEditing && this.form.id) {
          await updateEditorEmailTemplateProperty(this.form.id, payload, this.$keycloak.token);
        } else {
          await createEditorEmailTemplateProperty(payload, this.$keycloak.token);
        }

        this.$message.success({
          title: 'Variable saved',
        });
        this.dialogVisible = false;
        await this.loadProperties();
        this.$emit('properties-updated');
      } catch (err) {
        this.$message.error({
          title: 'Unable to save property',
          message: err.message,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.property-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/EmailTemplatePropertyManager.vue